// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { TrendingUp } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useProspectAccountsWithHighEngagement } from "@/hooks/api/use-prospect-accounts-with-high-engagement";
import { cn } from "@/lib/utils";

interface ProspectAccountsWithHighEngagementProps {
  days?: number;
  limit?: number;
  className?: string;
}

export function ProspectAccountsWithHighEngagement({
  days = 7,
  limit = 5,
  className,
}: ProspectAccountsWithHighEngagementProps) {
  const navigate = useNavigate();
  const {
    data: accounts,
    isLoading,
    error,
  } = useProspectAccountsWithHighEngagement(days, limit);

  const handleRowClick = (event: React.MouseEvent, accountId: string) => {
    const url = `/accounts/explorer?accountId=${accountId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <Card
      className={cn("w-full", className)}
      data-testid="prospect-accounts-card"
    >
      <CardHeader className="flex flex-row items-center gap-x-3 pb-2">
        <TrendingUp className="h-9 w-9 text-success flex-shrink-0" />
        <div className="space-y-1">
          <CardTitle className="text-md">
            <span>Growing prospect engagement</span>
          </CardTitle>
          <CardDescription>
            Selection of accounts with recent engagement spikes and no
            opportunities
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <div key={index}>
                  <Skeleton className="h-5 w-[200px]" />
                </div>
              ))}
          </div>
        ) : error ? (
          <div className="text-sm text-muted-foreground py-2">
            Error loading prospect accounts.
          </div>
        ) : !accounts?.length ? (
          <div className="text-sm text-muted-foreground py-2">
            No prospect accounts with growing engagement found.
          </div>
        ) : (
          <div className="space-y-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="whitespace-nowrap">
                    Engaging Via Channels
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <Tooltip key={account.account_id}>
                    <TooltipTrigger asChild>
                      <TableRow
                        onClick={(e) => handleRowClick(e, account.account_id)}
                        className="cursor-pointer hover:bg-accent group"
                        data-testid={`prospect-account-row-${account.account_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">
                                {account.account_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {account.account_type || "Account"}
                              </div>
                            </div>
                            <Link
                              to={`/accounts/${account.account_id}/explorer`}
                              onClick={(e) => e.stopPropagation()}
                            ></Link>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {account.engagement_channels &&
                            account.engagement_channels.length > 0 ? (
                              account.engagement_channels.map(
                                (channel, index) => (
                                  <Badge
                                    key={index}
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {channel.channel}
                                  </Badge>
                                ),
                              )
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                No channels
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View in account story</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
